import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { SheetScale } from '@otto/onscreen-engine';
import { useAuth } from '../lib/auth.tsx';
import {
  getSourcePdfSignedUrl,
  getSubmission,
  getTakeoffSession,
  listSubmissionPages,
  upsertTakeoffSession,
} from '../lib/db.ts';
import type { Submission, SubmissionPage, TakeoffSessionRow } from '../lib/types.ts';
import { Placeholder } from '../components/Placeholder.tsx';
import { CivilCanvas } from '../components/takeoff-civil/CivilCanvas.tsx';
import {
  EMPTY_JOB,
  hasAnyContent,
  restoreJobData,
  serializeJobData,
  setPage,
  type CivilJobData,
  type TakeoffItem,
} from '../components/takeoff-civil/helpers.ts';
import { COLORS, mono } from '../components/takeoff-civil/styles.ts';
import {
  ErrorBox,
  Loader,
  SaveBadge,
  type SaveStatus,
} from '../components/takeoff-civil/primitives.tsx';

export function TakeoffCivil() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <NoJobSelected />;
  return <TakeoffCivilWorkspace submissionId={id} />;
}

function NoJobSelected() {
  return (
    <Placeholder
      increment="Increment 5 — Civil takeoff"
      title="Takeoff · Civil"
      blurb="Open a job from the Jobs list to start a civil onscreen takeoff. Calibrate the sheet scale by clicking two points on a known dimension; the engine computes pixelsPerFoot for measurement tools coming next."
    />
  );
}

function TakeoffCivilWorkspace({ submissionId }: { submissionId: string }) {
  const { user } = useAuth();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [pages, setPages] = useState<readonly SubmissionPage[]>([]);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [sessionRow, setSessionRow] = useState<TakeoffSessionRow | null>(null);
  const [jobData, setJobData] = useState<CivilJobData>(EMPTY_JOB);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  // ── Load submission, pages, civil session ─────────────────────────────────
  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const [sub, pgs, session] = await Promise.all([
          getSubmission(submissionId),
          listSubmissionPages(submissionId),
          getTakeoffSession(submissionId, 'civil'),
        ]);
        if (!active) return;
        if (!sub) {
          setError('Submission not found.');
          return;
        }
        setSubmission(sub);
        setPages(pgs);
        setSessionRow(session);

        if (session && session.job_data && Object.keys(session.job_data).length > 0) {
          setJobData(restoreJobData(session.job_data));
        } else {
          setJobData(EMPTY_JOB);
        }

        const url = await getSourcePdfSignedUrl(submissionId, sub.source_pdf);
        if (!active) return;
        setSourceUrl(url);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Load failed');
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, [submissionId]);

  // ── Debounced save ─────────────────────────────────────────────────────────
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userId = user?.id ?? null;
  useEffect(() => {
    if (loading || !userId) return;
    setSaveStatus('dirty');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const row = await upsertTakeoffSession({
          submissionId,
          userId,
          discipline: 'civil',
          jobData: serializeJobData(jobData),
          weightSummary: null,
          status: hasAnyContent(jobData) ? 'in_progress' : 'draft',
        });
        setSessionRow(row);
        setSaveStatus('saved');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Save failed');
        setSaveStatus('error');
      }
    }, 600);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [jobData, userId, submissionId, loading]);

  const handleScaleSet = useCallback((scale: SheetScale, pageIndex: number) => {
    setJobData((curr) => setPage({ ...curr, pageIndex }, pageIndex, { scale }));
  }, []);

  const handlePageChange = useCallback((pageIndex: number) => {
    setJobData((curr) => ({ ...curr, pageIndex }));
  }, []);

  const handleItemsChange = useCallback(
    (pageIndex: number, updater: (prev: readonly TakeoffItem[]) => TakeoffItem[]) => {
      setJobData((curr) => {
        const prev = curr.pages[pageIndex]?.items ?? [];
        return setPage(curr, pageIndex, { items: updater(prev) });
      });
    },
    [],
  );

  if (loading) return <Loader text="Loading civil takeoff…" />;
  if (error) return <ErrorBox message={error} />;
  if (!submission || !sourceUrl) return <Loader text="Loading…" />;

  return (
    <div style={{ maxWidth: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Link
          to={`/app/jobs/${submissionId}`}
          style={{ ...mono(12, COLORS.textDim), textDecoration: 'none' }}
        >
          ← {submission.job_name}
        </Link>
        <div style={{ flex: 1 }} />
        <SaveBadge status={saveStatus} />
      </div>

      <CivilCanvas
        sourceUrl={sourceUrl}
        pages={pages}
        jobData={jobData}
        sessionRow={sessionRow}
        onScaleSet={handleScaleSet}
        onPageChange={handlePageChange}
        onItemsChange={handleItemsChange}
      />
    </div>
  );
}
